import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { zhTW, type MessageKey, type Messages } from './zh-TW';
import { en } from './en';

export type Locale = 'zh-TW' | 'en';

/** Traditional Chinese is the default; English is the second locale. */
export const LOCALES: readonly Locale[] = ['zh-TW', 'en'];
export const DEFAULT_LOCALE: Locale = 'zh-TW';

/** Native names, deliberately not translated. */
export const LOCALE_NAMES: Record<Locale, string> = {
  'zh-TW': '繁體中文',
  en: 'English',
};

const CATALOGUES: Record<Locale, Messages> = { 'zh-TW': zhTW, en };

/** BCP 47 tags for `Intl` and the document's `lang` attribute. */
const BCP47: Record<Locale, string> = { 'zh-TW': 'zh-Hant-TW', en: 'en' };

const STORAGE_KEY = 'journalmap.locale';

export type TranslateVars = Record<string, string | number>;

/** Substitute `{name}` placeholders. Unknown placeholders are left as-is so a
 *  missing variable shows up in review instead of silently disappearing. */
function interpolate(template: string, vars?: TranslateVars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = vars[name];
    return value === undefined ? whole : String(value);
  });
}

export interface I18n {
  locale: Locale;
  /** BCP 47 tag, for `Intl.NumberFormat` and friends. */
  tag: string;
  setLocale: (locale: Locale) => void;
  t: (key: MessageKey, vars?: TranslateVars) => string;
  /** Look a key up only if it exists; used for optional per-analysis hints. */
  has: (key: string) => key is MessageKey;
}

const I18nContext = createContext<I18n | null>(null);

function readStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'zh-TW') return stored;
  } catch {
    // Private browsing throws on access; the default is fine.
  }
  // Only switch away from Traditional Chinese for a browser that is clearly
  // not Chinese-reading. Anything zh-* stays on zh-TW.
  const nav = typeof navigator === 'undefined' ? '' : navigator.language;
  if (nav && !nav.toLowerCase().startsWith('zh')) return 'en';
  return DEFAULT_LOCALE;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readStoredLocale);

  useEffect(() => {
    document.documentElement.setAttribute('lang', BCP47[locale]);
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // Nothing to do; the choice just will not survive a reload.
    }
  }, [locale]);

  const value = useMemo<I18n>(() => {
    const catalogue = CATALOGUES[locale];
    return {
      locale,
      tag: BCP47[locale],
      setLocale: setLocaleState,
      t: (key, vars) => interpolate(catalogue[key], vars),
      has: (key): key is MessageKey => Object.prototype.hasOwnProperty.call(zhTW, key),
    };
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}

/** Shorthand for components that only need the translate function. */
export function useT(): I18n['t'] {
  return useI18n().t;
}

/** A stable formatter set for the active locale. */
export function useFormatters() {
  const { tag } = useI18n();
  return useMemo(() => {
    const integer = new Intl.NumberFormat(tag, { maximumFractionDigits: 0 });
    const decimal = new Intl.NumberFormat(tag, { maximumFractionDigits: 3 });
    const dateTime = new Intl.DateTimeFormat(tag, { dateStyle: 'medium', timeStyle: 'short' });
    const date = new Intl.DateTimeFormat(tag, { dateStyle: 'medium' });
    return {
      /** Whole numbers: item counts, link counts, document counts. */
      int: (n: number) => integer.format(n),
      /** Up to three decimals, for weights, scores and quality values. */
      num: (n: number) => (Number.isFinite(n) ? decimal.format(n) : '—'),
      dateTime: (d: Date) => dateTime.format(d),
      date: (d: Date) => date.format(d),
    };
  }, [tag]);
}

export type { MessageKey, Messages };
